/**
 * Hooks TanStack Query — bibliothèque de templates mockup (S4).
 *
 * Gestion CRUD du catalogue + matching publication. La preview composée
 * (bytes WebP) ne passe PAS par TanStack Query (binaire trop lourd à
 * cacher) — appel direct via `mockupTemplatesApi.preview()` depuis le
 * composant qui gère le `URL.createObjectURL`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { mockupTemplatesApi } from '@/utils/api/mockup-templates'
import type { TagResponse } from '@/types/works'
import type {
  BrowseForWorkParams,
  ListTemplatesParams,
  MatchForWorkParams,
  MockupTemplateAttachTagRequest,
  MockupTemplateCreatePayload,
  MockupTemplateMatchResponse,
  MockupTemplateResponse,
  MockupTemplateSummaryResponse,
  MockupTemplateUpdateRequest,
} from '@/types/mockup-templates'

// ──────────────────────────────────────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────────────────────────────────────

export const mockupTemplateKeys = {
  all: ['mockup-templates'] as const,
  list: (params: ListTemplatesParams) => ['mockup-templates', 'list', params] as const,
  detail: (id: number) => ['mockup-templates', 'detail', id] as const,
  match: (params: MatchForWorkParams) => ['mockup-templates', 'match', params] as const,
  browse: (params: BrowseForWorkParams) => ['mockup-templates', 'browse', params] as const,
}

// ──────────────────────────────────────────────────────────────────────────────
// Queries
// ──────────────────────────────────────────────────────────────────────────────

export function useMockupTemplates(params: ListTemplatesParams = {}) {
  return useQuery<MockupTemplateSummaryResponse[]>({
    queryKey: mockupTemplateKeys.list(params),
    queryFn: () => mockupTemplatesApi.list(params),
  })
}

export function useMockupTemplate(id: number | null) {
  return useQuery<MockupTemplateResponse>({
    queryKey: id !== null ? mockupTemplateKeys.detail(id) : ['mockup-templates', 'detail', null],
    queryFn: () => mockupTemplatesApi.get(id as number),
    enabled: id !== null,
  })
}

export function useMatchForWork(params: MatchForWorkParams | null) {
  return useQuery<MockupTemplateMatchResponse[]>({
    queryKey:
      params !== null ? mockupTemplateKeys.match(params) : ['mockup-templates', 'match', null],
    queryFn: () => mockupTemplatesApi.matchForWork(params as MatchForWorkParams),
    enabled: params !== null,
  })
}

/**
 * Liste TOUS les templates compatibles format pour une œuvre — mode picker
 * manuel. Pas de cap de couverture ni d'exclusion sticky. À privilégier sur
 * `useMatchForWork` quand l'objectif est la sélection manuelle (Stéphanie
 * choisit elle-même) et non la séquence de listing Etsy automatique.
 */
export function useBrowseForWork(params: BrowseForWorkParams | null) {
  return useQuery<MockupTemplateMatchResponse[]>({
    queryKey:
      params !== null ? mockupTemplateKeys.browse(params) : ['mockup-templates', 'browse', null],
    queryFn: () => mockupTemplatesApi.browseForWork(params as BrowseForWorkParams),
    enabled: params !== null,
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────────────────────────────────────

interface ApiError {
  detail: string
  status?: number
}

export function useCreateMockupTemplate() {
  const qc = useQueryClient()
  return useMutation<MockupTemplateResponse, ApiError, MockupTemplateCreatePayload>({
    mutationFn: (payload) => mockupTemplatesApi.create(payload),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: mockupTemplateKeys.all })
      qc.setQueryData(mockupTemplateKeys.detail(created.id), created)
    },
  })
}

export function useUpdateMockupTemplate() {
  const qc = useQueryClient()
  return useMutation<
    MockupTemplateResponse,
    ApiError,
    { id: number; payload: MockupTemplateUpdateRequest }
  >({
    mutationFn: ({ id, payload }) => mockupTemplatesApi.update(id, payload),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: mockupTemplateKeys.all })
      qc.setQueryData(mockupTemplateKeys.detail(updated.id), updated)
    },
  })
}

export function useDeleteMockupTemplate() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => mockupTemplatesApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mockupTemplateKeys.all })
    },
  })
}

export function useAttachTagToTemplate() {
  const qc = useQueryClient()
  return useMutation<
    TagResponse,
    ApiError,
    { id: number; payload: MockupTemplateAttachTagRequest }
  >({
    mutationFn: ({ id, payload }) => mockupTemplatesApi.attachTag(id, payload),
    onSuccess: (_tag, { id }) => {
      void qc.invalidateQueries({ queryKey: mockupTemplateKeys.detail(id) })
    },
  })
}

export function useDetachTagFromTemplate() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { id: number; tagId: number }>({
    mutationFn: ({ id, tagId }) => mockupTemplatesApi.detachTag(id, tagId),
    onSuccess: (_void, { id }) => {
      void qc.invalidateQueries({ queryKey: mockupTemplateKeys.detail(id) })
    },
  })
}

export function useRelaunchTemplateVision() {
  const qc = useQueryClient()
  return useMutation<
    { status: string; template?: MockupTemplateResponse },
    ApiError,
    { id: number; sync?: boolean }
  >({
    mutationFn: ({ id, sync = false }) => mockupTemplatesApi.relaunchVision(id, sync),
    onSuccess: (data, { id }) => {
      // Si sync, le template à jour est retourné — on update le cache
      // immédiatement. Sinon, on invalide le détail pour forcer un refetch
      // au prochain accès (l'UI poll en parallèle).
      if (data.template) {
        qc.setQueryData(mockupTemplateKeys.detail(id), data.template)
      } else {
        void qc.invalidateQueries({ queryKey: mockupTemplateKeys.detail(id) })
      }
    },
  })
}
